/**
 * Generic content editor.
 *
 * Field definitions are derived from the resource type, so one screen edits products, solutions,
 * services, industries, case studies, articles and pages. The workflow controls reflect the record's
 * current status and the user's capabilities: an Author sees "Submit for review" but not "Publish".
 *
 * Repeatable structured lists (features, specifications, workflow steps) are edited with a small
 * repeater rather than raw JSON, so an administrator never has to understand the storage format.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Select,
  Spinner,
  Tabs,
  TabPanel,
  Textarea,
} from '@/components/ui';
import { LoadError } from '@/components/site/blocks';
import { RevisionHistory } from '@/components/admin/RevisionHistory';
import { RepeaterField, type RepeaterShape } from '@/components/admin/RepeaterField';
import { useAuth } from '@/context/AuthContext';
import { CMS_RESOURCES, cmsApi, type CmsResourceKey } from '@/services/api';
import { ApiError } from '@/services/client';

type FieldType = 'text' | 'textarea' | 'richtext' | 'number' | 'checkbox' | 'select' | 'repeater' | 'stringlist' | 'date';

interface FieldSpec {
  name: string;
  label: string;
  type: FieldType;
  hint?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
  shape?: RepeaterShape;
  group: 'content' | 'detail' | 'seo' | 'settings';
}

const SEO_FIELDS: FieldSpec[] = [
  { name: 'seo_title', label: 'SEO title', type: 'text', group: 'seo', hint: 'Ideal length 50–60 characters. Blank uses the record title.' },
  {
    name: 'seo_description',
    label: 'Meta description',
    type: 'textarea',
    group: 'seo',
    hint: 'Ideal length 140–160 characters. Blank uses the summary.',
  },
  { name: 'seo_keywords', label: 'Keywords', type: 'text', group: 'seo', hint: 'Comma separated. Low impact on ranking, still used by some tools.' },
  { name: 'canonical_url', label: 'Canonical URL', type: 'text', group: 'seo', hint: 'Only set this if the content is published elsewhere first.' },
  { name: 'og_title', label: 'Social share title', type: 'text', group: 'seo' },
  { name: 'og_description', label: 'Social share description', type: 'textarea', group: 'seo' },
  { name: 'robots_index', label: 'Allow search engines to index this page', type: 'checkbox', group: 'seo' },
  { name: 'robots_follow', label: 'Allow search engines to follow links on this page', type: 'checkbox', group: 'seo' },
];

const COMMON_SETTINGS: FieldSpec[] = [
  { name: 'is_featured', label: 'Feature this record on listings and the homepage', type: 'checkbox', group: 'settings' },
  { name: 'order', label: 'Sort order', type: 'number', group: 'settings', hint: 'Lower numbers appear first.' },
  { name: 'needs_review', label: 'Still contains placeholder content', type: 'checkbox', group: 'settings', hint: 'Clear this once real information has been supplied.' },
  { name: 'review_note', label: 'Note for whoever completes it', type: 'textarea', group: 'settings' },
];

const TITLED_SHAPE: RepeaterShape = [
  { name: 'title', label: 'Title', type: 'text' },
  { name: 'description', label: 'Description', type: 'textarea' },
];

const RESOURCE_FIELDS: Record<string, FieldSpec[]> = {
  products: [
    { name: 'name', label: 'Product name', type: 'text', required: true, group: 'content' },
    { name: 'version', label: 'Version', type: 'text', group: 'content' },
    { name: 'tagline', label: 'Tagline', type: 'text', group: 'content' },
    { name: 'summary', label: 'Summary', type: 'textarea', required: true, group: 'content', hint: 'Shown on cards and in search results.' },
    { name: 'overview', label: 'Overview', type: 'richtext', group: 'content' },
    { name: 'icon', label: 'Icon name', type: 'text', group: 'content', hint: 'e.g. map-pin, video, cpu, database' },
    { name: 'features', label: 'Key features', type: 'repeater', group: 'detail', shape: TITLED_SHAPE },
    { name: 'benefits', label: 'Benefits', type: 'repeater', group: 'detail', shape: TITLED_SHAPE },
    {
      name: 'specifications',
      label: 'Technical specifications',
      type: 'repeater',
      group: 'detail',
      shape: [
        { name: 'group', label: 'Group', type: 'text' },
        { name: 'label', label: 'Specification', type: 'text' },
        { name: 'value', label: 'Value', type: 'text' },
      ],
    },
    { name: 'use_cases', label: 'Applications', type: 'repeater', group: 'detail', shape: TITLED_SHAPE },
    { name: 'integrations', label: 'Integrations', type: 'stringlist', group: 'detail' },
    { name: 'deployment_options', label: 'Deployment options', type: 'stringlist', group: 'detail' },
    { name: 'architecture_caption', label: 'Architecture caption', type: 'text', group: 'detail' },
    { name: 'architecture_alt_text', label: 'Architecture alt text', type: 'text', group: 'detail', hint: 'Describe the diagram for screen-reader users.' },
    { name: 'cta_heading', label: 'CTA heading', type: 'text', group: 'detail' },
    { name: 'cta_body', label: 'CTA body', type: 'textarea', group: 'detail' },
  ],
  solutions: [
    { name: 'name', label: 'Solution name', type: 'text', required: true, group: 'content' },
    { name: 'tagline', label: 'Tagline', type: 'text', group: 'content' },
    { name: 'summary', label: 'Summary', type: 'textarea', required: true, group: 'content' },
    { name: 'problem_statement', label: 'Problem statement', type: 'richtext', group: 'content' },
    { name: 'overview', label: 'Overview', type: 'richtext', group: 'content' },
    { name: 'icon', label: 'Icon name', type: 'text', group: 'content' },
    { name: 'capabilities', label: 'Capabilities', type: 'repeater', group: 'detail', shape: TITLED_SHAPE },
    { name: 'workflow_steps', label: 'Workflow steps', type: 'repeater', group: 'detail', shape: TITLED_SHAPE },
    { name: 'benefits', label: 'Benefits', type: 'repeater', group: 'detail', shape: TITLED_SHAPE },
    { name: 'use_cases', label: 'Use cases', type: 'repeater', group: 'detail', shape: TITLED_SHAPE },
    { name: 'architecture_caption', label: 'Architecture caption', type: 'text', group: 'detail' },
    { name: 'architecture_alt_text', label: 'Architecture alt text', type: 'text', group: 'detail' },
    { name: 'cta_heading', label: 'CTA heading', type: 'text', group: 'detail' },
    { name: 'cta_body', label: 'CTA body', type: 'textarea', group: 'detail' },
  ],
  services: [
    { name: 'name', label: 'Service name', type: 'text', required: true, group: 'content' },
    { name: 'tagline', label: 'Tagline', type: 'text', group: 'content' },
    { name: 'summary', label: 'Summary', type: 'textarea', required: true, group: 'content' },
    { name: 'overview', label: 'Overview', type: 'richtext', group: 'content' },
    { name: 'icon', label: 'Icon name', type: 'text', group: 'content' },
    { name: 'capabilities', label: 'Capabilities', type: 'stringlist', group: 'detail' },
    { name: 'deliverables', label: 'Deliverables', type: 'repeater', group: 'detail', shape: TITLED_SHAPE },
    { name: 'engagement_models', label: 'Engagement models', type: 'repeater', group: 'detail', shape: TITLED_SHAPE },
    { name: 'cta_heading', label: 'CTA heading', type: 'text', group: 'detail' },
    { name: 'cta_body', label: 'CTA body', type: 'textarea', group: 'detail' },
  ],
  industries: [
    { name: 'name', label: 'Industry name', type: 'text', required: true, group: 'content' },
    { name: 'short_name', label: 'Short name', type: 'text', group: 'content', hint: 'Used in filters and compact navigation.' },
    { name: 'summary', label: 'Summary', type: 'textarea', required: true, group: 'content' },
    { name: 'overview', label: 'Overview', type: 'richtext', group: 'content' },
    { name: 'icon', label: 'Icon name', type: 'text', group: 'content' },
    { name: 'accent_color', label: 'Accent colour', type: 'text', group: 'content', hint: 'Hex value, e.g. #1D4FD8' },
    { name: 'challenges', label: 'Sector challenges', type: 'repeater', group: 'detail', shape: TITLED_SHAPE },
    { name: 'outcomes', label: 'Target outcomes', type: 'repeater', group: 'detail', shape: TITLED_SHAPE },
    { name: 'compliance_notes', label: 'Compliance notes', type: 'richtext', group: 'detail' },
  ],
  'case-studies': [
    { name: 'title', label: 'Title', type: 'text', required: true, group: 'content' },
    { name: 'subtitle', label: 'Subtitle', type: 'text', group: 'content' },
    { name: 'summary', label: 'Summary', type: 'textarea', required: true, group: 'content' },
    { name: 'customer_name', label: 'Customer name', type: 'text', group: 'content', hint: 'Leave blank if the customer must stay anonymous.' },
    { name: 'customer_anonymised', label: 'Anonymise the customer', type: 'checkbox', group: 'content' },
    { name: 'customer_descriptor', label: 'Anonymous descriptor', type: 'text', group: 'content', hint: 'e.g. “A central government department”.' },
    { name: 'year', label: 'Year', type: 'number', group: 'content' },
    { name: 'duration_months', label: 'Duration (months)', type: 'number', group: 'content' },
    { name: 'location', label: 'Location', type: 'text', group: 'content' },
    { name: 'challenge', label: 'Challenge', type: 'richtext', group: 'detail' },
    { name: 'solution_narrative', label: 'Solution', type: 'richtext', group: 'detail' },
    { name: 'implementation', label: 'Implementation', type: 'richtext', group: 'detail' },
    { name: 'results', label: 'Results', type: 'richtext', group: 'detail' },
    { name: 'business_impact', label: 'Business impact', type: 'richtext', group: 'detail' },
    {
      name: 'metrics',
      label: 'Result metrics',
      type: 'repeater',
      group: 'detail',
      shape: [
        { name: 'label', label: 'Label', type: 'text' },
        { name: 'value', label: 'Value', type: 'text' },
        { name: 'unit', label: 'Unit', type: 'text' },
      ],
    },
    { name: 'quote', label: 'Customer quote', type: 'textarea', group: 'detail', hint: 'Only publish quotations you have written approval for.' },
    { name: 'quote_attribution', label: 'Quote attribution', type: 'text', group: 'detail' },
  ],
  articles: [
    {
      name: 'article_type',
      label: 'Content type',
      type: 'select',
      required: true,
      group: 'content',
      options: [
        { value: 'blog', label: 'Blog post' },
        { value: 'news', label: 'News' },
        { value: 'press_release', label: 'Press release' },
        { value: 'whitepaper', label: 'Whitepaper' },
        { value: 'brochure', label: 'Brochure' },
        { value: 'technical_article', label: 'Technical article' },
        { value: 'event', label: 'Event' },
      ],
    },
    { name: 'title', label: 'Title', type: 'text', required: true, group: 'content' },
    { name: 'subtitle', label: 'Subtitle', type: 'text', group: 'content' },
    { name: 'summary', label: 'Summary', type: 'textarea', required: true, group: 'content' },
    { name: 'body', label: 'Body', type: 'richtext', group: 'content' },
    { name: 'guest_author', label: 'Guest author', type: 'text', group: 'content', hint: 'For external contributors. Leave blank to use the account author.' },
    { name: 'author_role', label: 'Author role', type: 'text', group: 'content' },
    { name: 'event_starts_at', label: 'Event start', type: 'text', group: 'detail', hint: 'Events only. ISO format, e.g. 2026-09-14T10:00' },
    { name: 'event_location', label: 'Event location', type: 'text', group: 'detail' },
  ],
  pages: [
    { name: 'title', label: 'Page title', type: 'text', required: true, group: 'content' },
    { name: 'path', label: 'URL path', type: 'text', required: true, group: 'content', hint: 'Include the leading slash, e.g. /about/partners' },
    {
      name: 'template',
      label: 'Template',
      type: 'select',
      group: 'content',
      options: [
        { value: 'standard', label: 'Standard content page' },
        { value: 'landing', label: 'Campaign landing page' },
        { value: 'legal', label: 'Legal / policy page' },
        { value: 'wide', label: 'Full-width page' },
        { value: 'overview', label: 'Section overview page' },
      ],
    },
    { name: 'eyebrow', label: 'Eyebrow label', type: 'text', group: 'content' },
    { name: 'hero_heading', label: 'Hero heading', type: 'text', group: 'content' },
    { name: 'hero_subheading', label: 'Hero subheading', type: 'textarea', group: 'content' },
    { name: 'body', label: 'Body', type: 'richtext', group: 'content' },
    { name: 'hero_cta_label', label: 'Hero CTA label', type: 'text', group: 'detail' },
    { name: 'hero_cta_url', label: 'Hero CTA URL', type: 'text', group: 'detail' },
    { name: 'show_breadcrumbs', label: 'Show breadcrumbs', type: 'checkbox', group: 'settings' },
  ],
};

const GROUP_LABELS = { content: 'Content', detail: 'Detail', seo: 'SEO', settings: 'Settings' } as const;

function FieldInput({
  field,
  value,
  error,
  onChange,
}: {
  field: FieldSpec;
  value: unknown;
  error?: string;
  onChange: (value: unknown) => void;
}) {
  const id = `field-${field.name}`;

  if (field.type === 'checkbox') {
    return (
      <Checkbox id={id} label={field.label} checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />
    );
  }

  if (field.type === 'repeater') {
    return (
      <RepeaterField
        label={field.label}
        shape={field.shape ?? TITLED_SHAPE}
        value={Array.isArray(value) ? (value as Record<string, string>[]) : []}
        onChange={onChange}
        {...(field.hint ? { hint: field.hint } : {})}
      />
    );
  }

  if (field.type === 'stringlist') {
    const items = Array.isArray(value) ? (value as string[]) : [];
    return (
      <FormField label={field.label} htmlFor={id} {...(field.hint ? { hint: field.hint } : {})} {...(error ? { error } : {})}>
        <Textarea
          id={id}
          rows={4}
          value={items.join('\n')}
          onChange={(event) =>
            onChange(
              event.target.value
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean),
            )
          }
          error={Boolean(error)}
        />
        <p className="mt-1 text-xs text-muted">One item per line.</p>
      </FormField>
    );
  }

  const shared = { id, error: Boolean(error) } as const;

  return (
    <FormField
      label={field.label}
      htmlFor={id}
      {...(field.required ? { required: true } : {})}
      {...(field.hint ? { hint: field.hint } : {})}
      {...(error ? { error } : {})}
    >
      {field.type === 'select' ? (
        <Select {...shared} value={String(value ?? '')} onChange={(event) => onChange(event.target.value)}>
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      ) : field.type === 'richtext' ? (
        <>
          <Textarea {...shared} rows={12} value={String(value ?? '')} onChange={(event) => onChange(event.target.value)} className="font-mono text-sm" />
          <p className="mt-1 text-xs text-muted">
            Basic HTML is allowed (headings, paragraphs, lists, links, tables). Anything unsafe is stripped when saved.
          </p>
        </>
      ) : field.type === 'textarea' ? (
        <Textarea {...shared} rows={3} value={String(value ?? '')} onChange={(event) => onChange(event.target.value)} />
      ) : field.type === 'number' ? (
        <Input {...shared} type="number" value={value === null || value === undefined ? '' : String(value)} onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))} />
      ) : (
        <Input {...shared} value={String(value ?? '')} onChange={(event) => onChange(event.target.value)} />
      )}
    </FormField>
  );
}

export function ContentEditPage() {
  const { resource = '', slug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const config = CMS_RESOURCES[resource as CmsResourceKey];
  const isNew = !slug;

  const [form, setForm] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'danger' | 'warning'; message: string } | null>(null);

  const detailQuery = useQuery({
    queryKey: ['cms', resource, 'detail', slug],
    queryFn: () => cmsApi.detail(resource, slug!),
    enabled: Boolean(config) && !isNew,
  });

  useEffect(() => {
    if (detailQuery.data) setForm(detailQuery.data as Record<string, unknown>);
  }, [detailQuery.data]);

  const fields = useMemo(() => [...(RESOURCE_FIELDS[resource] ?? []), ...COMMON_SETTINGS, ...SEO_FIELDS], [resource]);

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      isNew ? cmsApi.create(resource, payload) : cmsApi.update(resource, slug!, payload),
    onSuccess: (record) => {
      setErrors({});
      setFeedback({ tone: 'success', message: isNew ? 'Created as a draft.' : 'Changes saved.' });
      void queryClient.invalidateQueries({ queryKey: ['cms', resource] });
      if (isNew) navigate(`/admin/${resource}/${record.slug}`, { replace: true });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isValidation) {
        setErrors(error.fields);
        setFeedback({ tone: 'danger', message: 'Some fields need attention — see the messages below.' });
      } else {
        setFeedback({ tone: 'danger', message: error instanceof ApiError ? error.message : 'Saving failed.' });
      }
    },
  });

  const transitionMutation = useMutation({
    mutationFn: (action: 'submit' | 'approve' | 'reject' | 'publish' | 'unpublish' | 'archive') =>
      cmsApi.transition(resource, slug!, action),
    onSuccess: (record, action) => {
      setForm(record as Record<string, unknown>);
      setFeedback({ tone: 'success', message: `Content ${action === 'reject' ? 'sent back to draft' : `${action}ed`}.` });
      void queryClient.invalidateQueries({ queryKey: ['cms', resource] });
      void queryClient.invalidateQueries({ queryKey: ['cms', 'dashboard'] });
    },
    onError: (error) => {
      setFeedback({
        tone: error instanceof ApiError && error.isConflict ? 'warning' : 'danger',
        message: error instanceof ApiError ? error.message : 'The action failed.',
      });
    },
  });

  if (!config) {
    return (
      <Alert tone="danger" title="Unknown content type">
        There is no editor for “{resource}”.
      </Alert>
    );
  }

  if (!isNew && detailQuery.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner label="Loading record" />
      </div>
    );
  }

  if (!isNew && detailQuery.isError) return <LoadError onRetry={() => void detailQuery.refetch()} />;

  const status = String(form.status ?? 'draft');
  const titleField = config.titleField;
  const recordTitle = String(form[titleField] ?? '') || `New ${config.label.replace(/s$/, '').toLowerCase()}`;

  function update(name: string, value: unknown) {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  }

  function handleSave() {
    // Send only editable fields; server-managed values would be rejected or silently ignored.
    const payload: Record<string, unknown> = {};
    for (const field of fields) {
      if (field.name in form) payload[field.name] = form[field.name];
    }
    saveMutation.mutate(payload);
  }

  const grouped = (['content', 'detail', 'seo', 'settings'] as const).map((group) => ({
    group,
    label: GROUP_LABELS[group],
    fields: fields.filter((field) => field.group === group),
  })).filter((entry) => entry.fields.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Link to={`/admin/${resource}`} className="text-sm text-muted no-underline hover:underline">
            ← Back to {config.label.toLowerCase()}
          </Link>
          <h1 className="mt-2 truncate font-display text-2xl font-bold text-ink">{recordTitle}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge tone={status === 'published' ? 'success' : status === 'review' ? 'signal' : 'neutral'}>{status}</Badge>
            {Boolean(form.needs_review) && <Badge tone="warning">Contains placeholder content</Badge>}
            {Boolean(form.slug) && <span className="font-mono text-xs text-muted">/{String(form.slug)}</span>}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {!isNew && status === 'draft' && (
            <Button variant="secondary" onClick={() => transitionMutation.mutate('submit')} loading={transitionMutation.isPending}>
              Submit for review
            </Button>
          )}
          {!isNew && status === 'review' && can('can_publish') && (
            <>
              <Button variant="secondary" onClick={() => transitionMutation.mutate('reject')}>
                Send back
              </Button>
              <Button variant="secondary" onClick={() => transitionMutation.mutate('approve')}>
                Approve
              </Button>
            </>
          )}
          {!isNew && can('can_publish') && status !== 'published' && (
            <Button onClick={() => transitionMutation.mutate('publish')} loading={transitionMutation.isPending}>
              Publish
            </Button>
          )}
          {!isNew && can('can_publish') && status === 'published' && (
            <Button variant="secondary" onClick={() => transitionMutation.mutate('unpublish')}>
              Unpublish
            </Button>
          )}
          <Button onClick={handleSave} loading={saveMutation.isPending}>
            {isNew ? 'Create draft' : 'Save changes'}
          </Button>
        </div>
      </div>

      {feedback && (
        <Alert tone={feedback.tone} title={feedback.tone === 'success' ? 'Saved' : feedback.tone === 'warning' ? 'Not allowed' : 'Problem'}>
          {feedback.message}
        </Alert>
      )}

      {Boolean(form.needs_review) && Boolean(form.review_note) && (
        <Alert tone="warning" title="This record needs real company information">
          {String(form.review_note)}
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <Card>
          <Tabs label="Record fields" items={grouped.map((entry) => ({ value: entry.group, label: entry.label }))}>
            {grouped.map((entry) => (
              <TabPanel key={entry.group} value={entry.group}>
                <div className="space-y-5">
                  {entry.fields.map((field) => (
                    <FieldInput
                      key={field.name}
                      field={field}
                      value={form[field.name]}
                      {...(errors[field.name] ? { error: errors[field.name] } : {})}
                      onChange={(value) => update(field.name, value)}
                    />
                  ))}
                </div>
              </TabPanel>
            ))}
          </Tabs>
        </Card>

        <aside className="space-y-6">
          <Card>
            <h2 className="text-base font-semibold text-ink">Publishing</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div>
                <dt className="text-muted">Status</dt>
                <dd className="mt-0.5 font-medium capitalize text-ink">{status}</dd>
              </div>
              {form.published_at ? (
                <div>
                  <dt className="text-muted">Published</dt>
                  <dd className="mt-0.5 text-ink">{new Date(String(form.published_at)).toLocaleString('en-IN')}</dd>
                </div>
              ) : null}
              {form.updated_at ? (
                <div>
                  <dt className="text-muted">Last updated</dt>
                  <dd className="mt-0.5 text-ink">{new Date(String(form.updated_at)).toLocaleString('en-IN')}</dd>
                </div>
              ) : null}
            </dl>
            {!can('can_publish') && (
              <p className="mt-4 rounded-md bg-raised p-3 text-xs text-muted">
                Your role can edit and submit for review. An Editor or Content Admin publishes.
              </p>
            )}
          </Card>

          {!isNew && slug && <RevisionHistory resource={resource} slug={slug} onRestored={() => void detailQuery.refetch()} />}

          <Card>
            <h2 className="text-base font-semibold text-ink">Media</h2>
            <p className="mt-2 text-sm text-muted">
              Hero images, architecture diagrams and datasheets are attached from the media library.
            </p>
            <Link to="/admin/media" className="mt-3 inline-block text-sm font-medium no-underline hover:underline">
              Open media library →
            </Link>
          </Card>
        </aside>
      </div>
    </div>
  );
}
